/**
 * The application's navigation map — one list, rendered twice: as the bottom
 * tab bar on a phone and as the sidebar from `lg:` up.
 *
 * `home` is the operational command dashboard the shell lands on. Everything
 * reachable from the sidebar is reachable on a phone too: the `bottomBar`
 * entries are the thumb-sized tabs, and the rest live one tap deeper, on the
 * "עוד" hub.
 *
 * What each entry stands behind is not decided here — `src/core/nav-access.ts`
 * owns that, so the sidebar, the tab bar and the hub cannot drift apart. This
 * module is the icons, the Hebrew and the split between the two navigations.
 */
import {
  ClipboardList,
  FileText,
  LayoutDashboard,
  Menu,
  Settings,
  Truck,
  Users,
  UsersRound,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { canSeeNavItem } from '../../core/nav-access';
import type { NavScreenId } from '../../core/nav-access';
import type { User } from '../../types/user';

/**
 * Written out rather than re-exported so the list reads here, where the map is.
 * `satisfies` is what keeps it honest: a screen added to this list with no
 * permission declared for it in `NAV_PERMISSIONS` will not compile.
 */
export const NAV_ITEM_IDS = [
  'home',
  'tasks',
  'vehicles',
  'soldiers',
  'reports',
  'users',
  'settings',
  'more',
] as const satisfies readonly NavScreenId[];

export type NavItemId = (typeof NAV_ITEM_IDS)[number];

export interface NavItem {
  id: NavItemId;
  label: string;
  icon: LucideIcon;
  /** Heading shown in the top bar while the screen is open. */
  title: string;
  /** Whether the entry earns one of the phone's bottom tabs. */
  bottomBar: boolean;
}

export const NAV_ITEMS: readonly NavItem[] = [
  { id: 'home', label: 'לוח בקרה', icon: LayoutDashboard, title: 'לוח בקרה מבצעי', bottomBar: true },
  { id: 'tasks', label: 'משימות', icon: ClipboardList, title: 'משימות שינוע', bottomBar: true },
  { id: 'vehicles', label: 'רכבים', icon: Truck, title: 'ניהול רכבים', bottomBar: true },
  { id: 'soldiers', label: 'חיילים', icon: Users, title: 'ניהול חיילים', bottomBar: true },
  { id: 'reports', label: 'דוחות', icon: FileText, title: 'דוחות ודף שילוח יומי', bottomBar: true },
  { id: 'users', label: 'ניהול משתמשים', icon: UsersRound, title: 'ניהול משתמשים מרכזי', bottomBar: false },
  { id: 'settings', label: 'הגדרות', icon: Settings, title: 'הגדרות מערכת ונתונים', bottomBar: false },
  { id: 'more', label: 'עוד', icon: Menu, title: 'חשבון והרשאות', bottomBar: true },
];

/** The tabs the phone shows; the rest are reached from the "עוד" hub. */
export const BOTTOM_BAR_ITEMS: readonly NavItem[] = NAV_ITEMS.filter((item) => item.bottomBar);

/** The destinations the "עוד" hub lists — everything not on the bottom bar. */
export const SECONDARY_ITEMS: readonly NavItem[] = NAV_ITEMS.filter(
  (item) => !item.bottomBar && item.id !== 'more',
);

export const navItem = (id: NavItemId): NavItem =>
  NAV_ITEMS.find((item) => item.id === id) ?? NAV_ITEMS[0];

/**
 * The entries this account may actually navigate to.
 *
 * A מפקד / בעל תפקיד loses "הגדרות" and "ניהול משתמשים" here and keeps
 * everything else, which is what the unit means by the two management levels.
 * The screens themselves are still guarded — this only stops offering a door
 * that would be refused.
 */
export const visibleNavItems = (
  user: User | null | undefined,
  items: readonly NavItem[] = NAV_ITEMS,
): NavItem[] => items.filter((item) => canSeeNavItem(user, item.id));
